import { useState } from "react";
import { KeyRound, Lock, Eye, EyeOff, Copy, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

// One credential row. `secret` fields stay masked until the admin explicitly
// reveals them, so the password is never sitting on screen during a demo or a
// screen-share.
function CredentialField({ label, value, secret = false }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const isMasked = secret && !revealed;

  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-bg-card)/60 dark:bg-black/30 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-(--color-text-muted) dark:text-gray-400">
        {label}
      </p>
      <div className="flex items-center gap-1 mt-1">
        <code className="flex-1 min-w-0 truncate font-mono text-sm text-(--color-text-primary)">
          {isMasked ? "••••••••••••" : value}
        </code>
        {secret && (
          <button
            type="button"
            onClick={() => setRevealed((prev) => !prev)}
            title={revealed ? "Hide" : "Reveal"}
            aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
            className="p-1.5 rounded-sm shrink-0 text-(--color-text-muted) dark:text-gray-400 hover:text-(--color-primary) hover:bg-(--color-primary)/10 transition-colors"
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={handleCopy}
          title="Copy"
          aria-label={`Copy ${label}`}
          className="p-1.5 rounded-sm shrink-0 text-(--color-text-muted) dark:text-gray-400 hover:text-(--color-primary) hover:bg-(--color-primary)/10 transition-colors"
        >
          {copied ? (
            <Check className="h-4 w-4 text-(--color-primary)" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// Injector Engine dashboard credentials for a scenario.
//
// The backend already gates the payload to platform_admin — both the catalog
// scenario detail and the team-scenario detail serialize `admin_credentials`
// as null for every other role. The role check here is a second gate so the
// card can never render for a non-admin regardless of what the response
// carries, and so both call sites stay gated identically.
export default function AdminCredentialsCard({ credentials }) {
  const { user } = useAuth();
  const isPlatformAdmin = (user?.roles || []).includes("platform_admin");

  if (!isPlatformAdmin || !credentials) return null;

  return (
    <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-3 sm:p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-500/10 rounded-md shrink-0">
          <KeyRound className="h-5 w-5 text-amber-400" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-(--color-text-primary)">
              Admin Credentials
            </p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-[10px] font-bold uppercase tracking-wider text-amber-400">
              <Lock className="h-2.5 w-2.5" />
              Platform Admin Only
            </span>
          </div>
          <p className="text-xs text-(--color-text-muted) dark:text-gray-400 mt-1">
            Visible to you because of your role. Never share these with players.
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {credentials.username && (
          <CredentialField label="Username" value={credentials.username} />
        )}
        {credentials.password && (
          <CredentialField
            label="Password"
            value={credentials.password}
            secret
          />
        )}
      </div>

      {credentials.note && (
        <p className="text-xs text-amber-400/90 leading-relaxed">
          {credentials.note}
        </p>
      )}
    </div>
  );
}
